/**
 * VoteSelector Component
 *
 * Story FOS-4.2.7: VotingPage Implementation
 * Task 4: Create VoteSelector component
 *
 * Radio-style selection for vote options with icons and colors.
 *
 * @see AC-4.2.7.1 - Vote selection shows in confirmation modal
 * @see AC-4.2.7.3 - NeedsRefinement triggers feedback input
 *
 * @module components/governance/VoteSelector
 */

import { ThumbsUp, ThumbsDown, MinusCircle, AlertCircle } from 'lucide-react';
import type { VoteChoice } from '../../services/governanceService';

// =============================================================================
// Types
// =============================================================================

export interface VoteSelectorProps {
  /** Currently selected vote option */
  selected: VoteChoice | null;
  /** Callback when vote option changes */
  onChange: (choice: VoteChoice) => void;
  /** Whether voting is disabled */
  disabled?: boolean;
}

// =============================================================================
// Constants
// =============================================================================

const VOTE_OPTIONS: {
  choice: VoteChoice;
  label: string;
  icon: typeof ThumbsUp;
  selectedClass: string;
}[] = [
  {
    choice: 'Yes',
    label: 'Yes',
    icon: ThumbsUp,
    selectedClass: 'bg-green-100 border-green-500 text-green-700 dark:bg-green-900/30 dark:border-green-500 dark:text-green-300',
  },
  {
    choice: 'No',
    label: 'No',
    icon: ThumbsDown,
    selectedClass: 'bg-red-100 border-red-500 text-red-700 dark:bg-red-900/30 dark:border-red-500 dark:text-red-300',
  },
  {
    choice: 'Abstain',
    label: 'Abstain',
    icon: MinusCircle,
    selectedClass: 'bg-gray-200 border-gray-400 text-gray-700 dark:bg-gray-700/30 dark:border-gray-500 dark:text-gray-300',
  },
  {
    choice: 'NeedsRefinement',
    label: 'Needs Refinement',
    icon: AlertCircle,
    selectedClass: 'bg-yellow-100 border-yellow-500 text-yellow-700 dark:bg-yellow-900/30 dark:border-yellow-500 dark:text-yellow-300',
  },
];

// =============================================================================
// Component
// =============================================================================

/**
 * Vote option selector with radio-button style selection
 */
export function VoteSelector({
  selected,
  onChange,
  disabled = false,
}: VoteSelectorProps) {
  return (
    <div
      className="flex flex-wrap gap-2"
      role="radiogroup"
      aria-label="Vote options"
      data-testid="vote-selector"
    >
      {VOTE_OPTIONS.map(({ choice, label, icon: Icon, selectedClass }) => {
        const isSelected = selected === choice;

        return (
          <button
            key={choice}
            type="button"
            role="radio"
            aria-checked={isSelected}
            disabled={disabled}
            onClick={() => onChange(choice)}
            className={`
              flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors
              ${isSelected ? selectedClass : 'border-border hover:bg-muted/50'}
              ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
            `}
            data-testid={`vote-option-${choice.toLowerCase()}`}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        );
      })}
    </div>
  );
}

export default VoteSelector;
